import React, { useEffect, useMemo, useState, useRef } from "react";
import LoadingOverlay from "react-loading-overlay";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";

import { httpGetShippingLocationDetails } from "../../../../service/httpClient";
import CustomGridToolTip from "../../../Common/Tooltip/CustomGridToolTip";
import ShippingTitleName from "../TabHeader/ShippingTitleName";
import "../../../Common/Tooltip/CustomGridToolTip.css";
import "./ShippingLocationDetails.css";
import * as Constant from "../../../../constants/Constants";
import * as RolePermissionConstants from "../../../../constants/RolePermissionConstants";
import { getUserRolePermission } from "../../../../utils/rolePermissionAccess";

const stringDataComparator = (valueA, valueB) => {
  const valueALower =
    valueA && valueA !== "" ? valueA.toLowerCase().trim() : valueA;
  const valueBLower =
    valueB && valueB !== "" ? valueB.toLowerCase().trim() : valueB;
  if (valueALower && valueBLower) {
    return valueALower === valueBLower
      ? 0
      : valueALower.localeCompare(valueBLower, "en", { numeric: true });
  } else if (valueALower && (valueBLower === null || valueBLower === "")) {
    return -1;
  } else if ((valueALower === null || valueALower === "") && valueBLower) {
    return 1;
  } else {
    return -1;
  }
};

const stringNumericDataComparator = (valueA, valueB) => {
  const valueALower =
    valueA && valueA !== "" ? valueA.toLowerCase().trim() : valueA;
  const valueBLower =
    valueB && valueB !== "" ? valueB.toLowerCase().trim() : valueB;
  if (valueALower && valueBLower) {
    if (!isNaN(valueALower) && !isNaN(valueBLower)) {
      return parseInt(valueALower, 10) === parseInt(valueBLower, 10)
        ? 0
        : parseInt(valueALower, 10) > parseInt(valueBLower, 10)
        ? 1
        : -1;
    } else if (isNaN(valueALower) && !isNaN(valueBLower)) {
      return 1;
    } else if (!isNaN(valueALower) && isNaN(valueBLower)) {
      return -1;
    } else {
      return valueALower === valueBLower
        ? 0
        : valueALower.localeCompare(valueBLower, "en", { numeric: true });
    }
  } else if (valueALower && (valueBLower === null || valueBLower === "")) {
    return -1;
  } else if ((valueALower === null || valueALower === "") && valueBLower) {
    return 1;
  } else {
    return -1;
  }
};

const defaultColDef = {
  sortable: true,
  enablePivot: true,
  filter: true,
  resizable: true,
  tooltipComponent: CustomGridToolTip,
};

const ShippingLocationDetails = (props) => {
  const [responseData, setResponseData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const vendorProfileId = localStorage.getItem("vid") || null;
  const gridRef = useRef();
  const addOrEditshippingRolePermission =
    getUserRolePermission(
      RolePermissionConstants.VENDORPLATFORM_SHIPPING_LOCATIONDETAILS,
      RolePermissionConstants.ALL
    ) !== RolePermissionConstants.NONE;
  const columnDefs = [
    {
      headerName: "Location ID",
      field: "locationId",
      width: 150,
      cellRenderer: (params) => {
        const { data = {} } = params || {};
        const { vendorProfileId = "", locationId = "" } = data || {};
        if (addOrEditshippingRolePermission) {
          return (
            <a
              className="locationIdLink"
              onClick={(e) => {
                navigateLocation(e, data);
              }}
              href={`#`}
              target="_self"
            >
              {locationId}
            </a>
          );
        } else {
          return <div> {locationId}</div>;
        }
      },
      comparator: stringNumericDataComparator,
    },
    {
      headerName: "Location Name",
      field: "locationName",
      width: 180,
      comparator: stringNumericDataComparator,
    },
    {
      headerName: "Address",
      field: "locationAddress",
      width: 400,
      comparator: stringNumericDataComparator,
    },
    {
      headerName: "Status",
      field: "locationStatus",
      width: 150,
      comparator: stringDataComparator,
    },
    {
      headerName: "Open 24/7",
      field: "isAllDayOperation",
      width: 150,
      comparator: stringDataComparator,
    },
  ];
  const navigateLocation = (e, data) => {
    e.preventDefault();
    props.history.push({
      pathname: `/${Constant.BOARDING}/${Constant.SHIPPING}/${Constant.SHIPPING_LOCATION}`,
      param: "edit",
      state: data,
    });
  };
  useEffect(() => {
    vendorProfileId !== null && responseData?.length === 0
      ? httpGetShippingLocationDetails(vendorProfileId)
          .then((response) => {
            const { data = [], status } = response || {};

            if (data?.length > 0 && status === 200) {
              const locationDetailsResp =
                response?.data && response?.data.length > 0
                  ? response?.data
                  : [];
              const locationDetailsList =
                locationDetailsResp?.length > 0 &&
                locationDetailsResp.map((item) => {
                  return {
                    ...item,
                    locationAddress:
                      item.streetName +
                      " " +
                      item.city +
                      " " +
                      item.state +
                      " " +
                      item.zipCode +
                      " " +
                      item.country,
                  };
                });

              setResponseData(locationDetailsList);
              setIsLoading(false);
            } else {
              setIsLoading(false);
            }
          })
          .catch((error) => {
            setIsLoading(false);
            console.log(error);
          })
      : setIsLoading(false);
  }, [responseData, isLoading, vendorProfileId]);

  const sideBar = useMemo(() => {
    return {
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnExpandAll: true,
          },
        },
      ],
    };
  }, []);

  return (
    <div className="shippingLocation">
      <LoadingOverlay active={isLoading} spinner>
        <ShippingTitleName
          title={Constant.VENDORPLATFORM_SUB_HEAD_LOCATIONDETAILS}
          showAddBtn={true}
          vendorProfileId={vendorProfileId}
          addOrEditshippingRolePermission={addOrEditshippingRolePermission}
        />
        <div className="ag-theme-balham shippingLocationTable">
          <AgGridReact
            columnDefs={columnDefs}
            rowData={responseData}
            defaultColDef={defaultColDef}
            ref={gridRef}
            multiSortKey={"ctrl"}
            maintainColumnOrder={true}
            rowSelection="multiple"
            paginationPageSize="20"
            pagination={true}
            overlayNoRowsTemplate={
              responseData.length > 0 ? "" : "No Shipping Locations Yet"
            }
            enableRangeSelection={true}
            sideBar={sideBar}
            suppressDragLeaveHidesColumns={true}
          />
        </div>
      </LoadingOverlay>
    </div>
  );
};

export default ShippingLocationDetails;
